import { FormattedMessage } from "react-intl";

import { Card } from "common/core/card";
import { Heading } from "common/core/typography";
import { DescriptionList, DescriptionListItem } from "common/core/description_list";
import { Badge } from "common/core/badge";
import { format } from "common/core/format/date";
import OverallRiskLevel from "common/details/identity/common";
import { InfoTooltip } from "common/details/identity/v2/components/info_tooltip";
// Components reused from V1
import SeenBefore from "common/details/identity/signer/email_risk/seen_before";
import EmailAge from "common/details/identity/signer/email_risk/email_age";
import MatchesIdentity from "common/details/identity/signer/email_risk/matches_identity";
import { LineTypeLabels } from "common/details/identity/signer/phone_risk";

import Styles from "./index.module.scss";
import type { RiskSignalsCustomerSigner as CustomerSigner } from "./index.fragment.graphql";

export function EmailRiskSignals({
  customerSigner: { emailRiskSignal },
}: {
  customerSigner: CustomerSigner;
}) {
  return (
    <Card fullWidth noMargin className={Styles.container}>
      <div className={Styles.heading}>
        <Heading level="h4" textStyle="allCapsLabelSmall">
          <FormattedMessage
            id="a0e46327-6ff9-4186-9ee6-04fb482608d0"
            defaultMessage="Email Risk Signals"
          />
        </Heading>
        <OverallRiskLevel riskLevel={emailRiskSignal?.overallRiskLevel} />
      </div>
      <DescriptionList direction="vertical" itemGap="none" className={Styles.list}>
        <DescriptionListItem
          term={
            <>
              <FormattedMessage
                id="c3bf96b7-0f96-496b-ad4b-9c2e4733ebf4"
                defaultMessage="Seen on network"
              />
              <InfoTooltip>
                <FormattedMessage
                  id="724747cb-df53-4563-ad98-4745eb2e1a1a"
                  defaultMessage="Previously seen email address on our network"
                />
              </InfoTooltip>
            </>
          }
          definition={<SeenBefore seenBefore={emailRiskSignal?.seenBefore} withBadge={false} />}
          direction="row"
        />
        <DescriptionListItem
          term={
            <FormattedMessage
              id="f66870b0-7bef-48b8-b7d8-6c4ebc28a8fb"
              defaultMessage="Email age"
            />
          }
          definition={<EmailAge emailAge={emailRiskSignal?.emailAge} withBadge={false} />}
          direction="row"
        />
        <DescriptionListItem
          term={
            <>
              <FormattedMessage
                id="3aba6652-f46f-46c8-8e5a-cfcf0f1269fd"
                defaultMessage="Matches identity"
              />
              <InfoTooltip>
                <FormattedMessage
                  id="a823cd19-ce00-40a0-900b-5cad860953a6"
                  defaultMessage="Identity is associated with this email address"
                />
              </InfoTooltip>
            </>
          }
          definition={
            <MatchesIdentity matchesIdentity={emailRiskSignal?.matchesIdentity} withBadge={false} />
          }
          direction="row"
        />
      </DescriptionList>
    </Card>
  );
}

export function PhoneRiskSignals({
  customerSigner: { phone, phoneRiskSignal },
}: {
  customerSigner: CustomerSigner;
}) {
  return (
    <Card fullWidth noMargin className={Styles.container}>
      <div className={Styles.heading}>
        <Heading level="h4" textStyle="allCapsLabelSmall">
          <FormattedMessage
            id="b28b2982-ac69-4b7c-b902-cf408df7b004"
            defaultMessage="Phone Risk Signals"
          />
        </Heading>
        {!phone && (
          <Badge kind="infoSubtle">
            <FormattedMessage
              id="8c62b671-7a43-4af2-9fe9-d5d3f02d83ed"
              defaultMessage="Not provided"
            />
          </Badge>
        )}
      </div>

      <DescriptionList direction="vertical" itemGap="none" className={Styles.list}>
        <DescriptionListItem
          term={
            <>
              <FormattedMessage
                id="1fbf2fa4-c991-4357-94c2-89a02ccd4990"
                defaultMessage="Line type"
              />
              <InfoTooltip>
                <FormattedMessage
                  id="e6b7e797-493e-47b5-8904-d764cdae18f5"
                  defaultMessage="VoIP could be an indicator of fraud "
                />
              </InfoTooltip>
            </>
          }
          definition={
            phoneRiskSignal?.phoneLineType ? LineTypeLabels[phoneRiskSignal.phoneLineType] : "--"
          }
          direction="row"
        />
        <DescriptionListItem
          term={
            <FormattedMessage
              id="64e42cf6-a535-450a-9310-085d3b5aa932"
              defaultMessage="Name of carrier"
            />
          }
          definition={phoneRiskSignal?.carrier ?? "--"}
          direction="row"
        />
        <DescriptionListItem
          term={
            <>
              <FormattedMessage
                id="e1089089-a429-427f-9a5b-6d56b3e530ea"
                defaultMessage="Last SIM swap"
              />
              <InfoTooltip>
                <FormattedMessage
                  id="508e6fd6-b1d2-49c6-9bb4-6206662f0ca3"
                  defaultMessage="A recent change in SIM could be an indicator of fraud"
                />
              </InfoTooltip>
            </>
          }
          definition={
            phoneRiskSignal?.lastSimSwap
              ? format({ value: phoneRiskSignal.lastSimSwap, formatStyle: "LL/dd/yyyy" })
              : "--"
          }
          direction="row"
        />
      </DescriptionList>
    </Card>
  );
}
